import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase/config";
import {
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  doc,
  orderBy,
} from "firebase/firestore";

const AdminAssignDeliveries = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [filteredDeliveries, setFilteredDeliveries] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [areaFilter, setAreaFilter] = useState("");
  const [slotFilter, setSlotFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateRange, setDateRange] = useState("today"); // today, week, month, custom
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('assign'); // 'assign' or 'logs'

  useEffect(() => {
    fetchData();
  }, [dateRange, customStartDate, customEndDate]);

  useEffect(() => {
    filterDeliveries();
  }, [deliveries, searchTerm, areaFilter, slotFilter, statusFilter, showUnassignedOnly]);

  const fetchData = async () => {
    setLoading(true);

    try {
      let startDate, endDate;
      const today = new Date().toISOString().split("T")[0];

      // Calculate date range based on selection
      switch (dateRange) {
        case "today":
          startDate = today;
          endDate = today;
          break;
        case "week":
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          startDate = weekAgo.toISOString().split("T")[0];
          endDate = today;
          break;
        case "month":
          const monthAgo = new Date();
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          startDate = monthAgo.toISOString().split("T")[0];
          endDate = today;
          break;
        case "custom":
          startDate = customStartDate;
          endDate = customEndDate;
          break;
        default:
          startDate = today;
          endDate = today;
      }

      // Fetch deliveries for the date range
      const deliveryQuery = query(
        collection(db, "deliveries"),
        where("date", ">=", startDate),
        where("date", "<=", endDate),
        orderBy("date", "desc")
      );
      const deliverySnapshot = await getDocs(deliveryQuery);

      // Fetch all subscriptions to enrich delivery data
      const subscriptionsSnapshot = await getDocs(collection(db, "subscriptions"));
      const subscriptionsMap = {};
      subscriptionsSnapshot.docs.forEach(doc => {
        subscriptionsMap[doc.id] = { id: doc.id, ...doc.data() };
      });

      // Process delivery data with subscription end dates
      const processedDeliveries = deliverySnapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        
        // Get subscription data for this delivery
        const subscription = subscriptionsMap[data.userId] || {};
        
        // Calculate original and extended end dates
        const originalEndDate = subscription.originalEndDate || subscription.endDate || null;
        const missedDays = subscription.missedDays || 0;
        const projectedEndDate = subscription.projectedEndDate || null;

        return {
          id: docSnap.id,
          // Prioritize delivery data, fallback to subscription data
          customerName: data.customerName || data.name || subscription.name || subscription.userName || "Unknown Customer",
          customerPhone: data.customerPhone || data.phone || subscription.phone || "N/A",
          planName: data.planName || subscription.planName || "Standard Plan",
          area: data.area || subscription.area || "Unknown",
          address: data.address || subscription.addressDetails || subscription.address || "No Address",
          slot: data.slot || subscription.deliverySlot || "Morning",
          status: data.status || "pending",
          date: data.date,
          assignedPartnerId: data.assignedPartnerId,
          assignedAt: data.assignedAt,
          // Subscription end dates
          originalEndDate,
          projectedEndDate,
          missedDays,
        };
      });

      // Fetch approved delivery partners
      const partnersQuery = query(
        collection(db, "users"),
        where("role", "==", "deliveryPartner"),
        where("status", "==", "approved")
      );
      const partnersSnapshot = await getDocs(partnersQuery);
      const approvedPartners = partnersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setDeliveries(processedDeliveries);
      setPartners(approvedPartners);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterDeliveries = () => {
    let filtered = [...deliveries];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(d => 
        d.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.customerPhone?.includes(searchTerm) ||
        d.area?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by area
    if (areaFilter) {
      filtered = filtered.filter(d => d.area === areaFilter);
    }

    // Filter by slot
    if (slotFilter) {
      filtered = filtered.filter(d => d.slot === slotFilter);
    }

    // Filter by status
    if (statusFilter) {
      filtered = filtered.filter(d => d.status === statusFilter);
    }

    // Filter by unassigned only
    if (showUnassignedOnly) {
      filtered = filtered.filter(d => !d.assignedPartnerId);
    }

    setFilteredDeliveries(filtered);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  const handleAssign = async (deliveryId, partnerId) => {
    if (!partnerId) return;
    
    try {
      await updateDoc(doc(db, "deliveries", deliveryId), {
        assignedPartnerId: partnerId,
        assignedAt: new Date().toISOString(),
      });
      alert("✅ Delivery assigned successfully");
      fetchData(); // Refresh the list
    } catch (error) {
      console.error("Error assigning delivery:", error);
      alert("❌ Failed to assign delivery");
    }
  };

  const handleReassign = async (deliveryId, partnerId) => {
    if (!partnerId) return;

    try {
      await updateDoc(doc(db, "deliveries", deliveryId), {
        assignedPartnerId: partnerId,
        reassignedAt: new Date().toISOString(),
      });
      alert("✅ Delivery reassigned successfully");
      fetchData(); // Refresh
    } catch (error) {
      console.error("Error reassigning delivery:", error);
      alert("❌ Failed to reassign delivery");
    }
  };

  const groupByArea = (items) => {
    return items.reduce((acc, item) => {
      const area = item.area || "Unknown";
      if (!acc[area]) acc[area] = [];
      acc[area].push(item);
      return acc;
    }, {});
  };

  const deliveriesByArea = groupByArea(filteredDeliveries);

  // Get unique areas, slots, and statuses for filters
  const uniqueAreas = [...new Set(deliveries.map(d => d.area).filter(Boolean))];
  const uniqueSlots = [...new Set(deliveries.map(d => d.slot).filter(Boolean))];
  const uniqueStatuses = [...new Set(deliveries.map(d => d.status).filter(Boolean))];

  const getStatusColor = (status) => {
    switch (status) {
      case "delivered": return "bg-green-100 text-green-800";
      case "pending": return "bg-yellow-100 text-yellow-800";
      case "in_transit": return "bg-blue-100 text-blue-800";
      case "not_delivered": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getSlotColor = (slot) => {
    return slot === "Morning" ? "bg-orange-100 text-orange-800" : "bg-purple-100 text-purple-800";
  };

  return (
    <div className="min-h-screen bg-green-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-green-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/admin/dashboard")}
                className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 font-medium rounded-md hover:bg-green-200 transition-colors"
              >
                ← Back to Dashboard
              </button>
              <h1 className="text-2xl font-bold text-green-700">
                Assign Deliveries
              </h1>
            </div>
            <button
              onClick={handleRefresh}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                refreshing
                  ? "bg-gray-200 text-gray-600"
                  : "bg-green-600 text-white hover:bg-green-700"
              }`}
              disabled={refreshing}
            >
              {refreshing ? "Refreshing..." : "🔄 Refresh"}
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-green-200 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <input
                type="text"
                placeholder="Customer name, phone, area..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              />
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date Range
              </label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              >
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {/* Area Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Area
              </label>
              <select
                value={areaFilter}
                onChange={(e) => setAreaFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              >
                <option value="">All Areas</option>
                {uniqueAreas.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </div>

            {/* Slot Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Slot
              </label>
              <select
                value={slotFilter}
                onChange={(e) => setSlotFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              >
                <option value="">All Slots</option>
                {uniqueSlots.map(slot => (
                  <option key={slot} value={slot}>{slot}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Additional Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              >
                <option value="">All Statuses</option>
                {uniqueStatuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            {/* Custom Date Range */}
            {dateRange === "custom" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </>
            )}

            {/* Show Unassigned Only */}
            <div className="flex items-center">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showUnassignedOnly}
                  onChange={(e) => setShowUnassignedOnly(e.target.checked)}
                  className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <span className="ml-2 text-sm text-gray-700">Show unassigned only</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 max-w-7xl mx-auto">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
            <p className="text-green-600">Loading deliveries...</p>
          </div>
        ) : filteredDeliveries.length === 0 ? (
          <div className="bg-white p-8 rounded-xl text-center shadow">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              {deliveries.length === 0 ? "No deliveries found" : "No deliveries match your filters"}
            </h3>
            <p className="text-gray-500">
              {deliveries.length === 0 
                ? "There are no deliveries in the selected date range." 
                : "Try adjusting your search criteria or filters."}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-green-600">{filteredDeliveries.length}</div>
                  <div className="text-sm text-gray-600">Total Deliveries</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-600">
                    {filteredDeliveries.filter(d => !d.assignedPartnerId).length}
                  </div>
                  <div className="text-sm text-gray-600">Unassigned</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">
                    {filteredDeliveries.filter(d => d.status === "delivered").length}
                  </div>
                  <div className="text-sm text-gray-600">Delivered</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-yellow-600">
                    {filteredDeliveries.filter(d => d.status === "pending").length}
                  </div>
                  <div className="text-sm text-gray-600">Pending</div>
                </div>
              </div>
            </div>

            {/* UI Tabs */}
            <div className="flex gap-4 mb-6">
              <button
                className={`px-4 py-2 rounded ${activeTab === 'assign' ? 'bg-green-600 text-white' : 'bg-white text-green-700 border border-green-200'}`}
                onClick={() => setActiveTab('assign')}
              >
                Assign/Reassign Deliveries
              </button>
              <button
                className={`px-4 py-2 rounded ${activeTab === 'logs' ? 'bg-green-600 text-white' : 'bg-white text-green-700 border border-green-200'}`}
                onClick={() => setActiveTab('logs')}
              >
                Delivery Update Logs
              </button>
            </div>

                        {activeTab === 'assign' && (
              <>
                {/* Deliveries by Area */}
                {Object.entries(deliveriesByArea).map(([area, areaDeliveries]) => (
                  <div key={area} className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="bg-green-50 px-4 py-3 border-b border-green-200">
                      <h2 className="text-lg font-semibold text-green-700">
                        📍 {area} ({areaDeliveries.length} deliveries)
                      </h2>
                    </div>
                    <div className="p-4 space-y-4">
                      {areaDeliveries.map((delivery) => (
                        <div
                          key={delivery.id}
                          className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-semibold text-gray-900">{delivery.customerName}</h3>
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(delivery.status)}`}>
                                  {delivery.status}
                                </span>
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSlotColor(delivery.slot)}`}>
                                  {delivery.slot}
                                </span>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                                <div>📱 {delivery.customerPhone}</div>
                                <div>📅 {delivery.date}</div>
                                <div>📦 {delivery.planName}</div>
                                <div>📍 {delivery.area}</div>
                              </div>
                              {delivery.address && (
                                <div className="text-sm text-gray-500 mt-1">
                                  📍 {delivery.address}
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col gap-2 min-w-[200px]">
                              <select
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500 text-sm"
                                onChange={(e) => handleAssign(delivery.id, e.target.value)}
                                value={delivery.assignedPartnerId || ""}
                              >
                                <option value="">Select a delivery partner</option>
                                {partners.map((partner) => (
                                  <option key={partner.id} value={partner.id}>
                                    {partner.name} ({partner.email})
                                  </option>
                                ))}
                              </select>
                              {delivery.assignedPartnerId && (
                                <p className="text-xs text-blue-600">
                                  Assigned to: {partners.find(p => p.id === delivery.assignedPartnerId)?.name || delivery.assignedPartnerId}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            )}
            
            {activeTab === 'logs' && (
              <div className="bg-white p-6 rounded-xl shadow-sm border border-green-100">
                <h2 className="text-lg font-semibold mb-4 text-green-700">Delivery Update Logs</h2>
                <table className="min-w-full text-sm text-left">
                  <thead className="bg-green-100 text-green-800">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Delivery ID</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold">Updated By</th>
                      <th className="px-4 py-3 font-semibold">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDeliveries.flatMap(delivery =>
                      (delivery.statusUpdates || []).map((update, idx) => (
                        <tr key={delivery.id + '-' + idx}>
                          <td className="px-4 py-2">{delivery.id}</td>
                          <td className="px-4 py-2">{update.status}</td>
                          <td className="px-4 py-2">{update.updatedBy || 'N/A'}</td>
                          <td className="px-4 py-2">{update.time ? new Date(update.time).toLocaleString() : 'N/A'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                <p className="text-xs text-gray-500 mt-2">* Only deliveries with status updates are shown here.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAssignDeliveries; 